import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

const GAP = 4;
const EDGE = 8;

/**
 * Positions a portal-rendered popover under (or above, when there is no room)
 * an anchor element, using fixed viewport coordinates so it escapes any
 * overflow/z-index context (modals, cards, tables).
 *
 * Also wires outside-click closing and re-positioning on scroll / resize /
 * popover size changes.
 */
const useAnchoredPopover = ({
  open,
  anchorRef,
  popRef,
  onClose,
  matchWidth = false,
  minWidth = 0,
}) => {
  const [style, setStyle] = useState({ visibility: 'hidden' });

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const pop = popRef.current;
    if (!anchor || !pop) return;

    const rect = anchor.getBoundingClientRect();
    const popHeight = pop.offsetHeight;
    const width = matchWidth ? Math.max(rect.width, minWidth) : pop.offsetWidth;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    const spaceBelow = viewportH - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;
    const placeAbove = spaceBelow < popHeight && spaceAbove > spaceBelow;

    const top = placeAbove
      ? Math.max(EDGE, rect.top - GAP - popHeight)
      : rect.bottom + GAP;
    let left = rect.left;
    if (left + width > viewportW - EDGE) {
      left = Math.max(EDGE, viewportW - EDGE - width);
    }

    setStyle({
      position: 'fixed',
      top,
      left,
      width: matchWidth ? width : undefined,
      zIndex: 1080,
      visibility: 'visible',
    });
  }, [anchorRef, popRef, matchWidth, minWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle({ visibility: 'hidden' });
      return undefined;
    }
    update();
    if (typeof ResizeObserver === 'undefined' || !popRef.current) {
      return undefined;
    }
    const observer = new ResizeObserver(() => update());
    observer.observe(popRef.current);
    return () => observer.disconnect();
  }, [open, update, popRef]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      const inAnchor = anchorRef.current?.contains(e.target);
      const inPop = popRef.current?.contains(e.target);
      if (!inAnchor && !inPop) onClose();
    };

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, update, onClose, anchorRef, popRef]);

  return style;
};

export default useAnchoredPopover;
